import { useState, useCallback, useRef } from 'react';
import StarXpandPlugin from 'capacitor-starxpand';
import EposPlugin from 'capacitor-epos';

type PrinterBrand = 'star' | 'epson';

interface DiscoveredPrinter {
  name: string;
  address: string;
  brand: 'epson' | 'star';
  signalStrength?: number;
  isPrinter: boolean;
}

const SCAN_TIMEOUT_MS = 15000;

export const usePrinterDiscovery = (bluetoothEnabled: boolean) => {
  const [isScanning, setIsScanning] = useState(false);
  const [discoveredPrinters, setDiscoveredPrinters] = useState<DiscoveredPrinter[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);

  const scanEpsonPrinters = useCallback(async (): Promise<DiscoveredPrinter[]> => {
    try {
      // On iOS, also fetch the list of Epson printers already paired via
      // iOS Settings > Bluetooth (ExternalAccessory / MFi accessories).
      // These will NOT appear in Epos2Discovery unless ExternalAccessory
      // is properly linked — merging them here ensures they're always shown.
      let pairedList: DiscoveredPrinter[] = [];
      try {
        const paired = await (EposPlugin as any).getPairedBluetoothPrinters?.();
        if (paired?.printers && Array.isArray(paired.printers)) {
          pairedList = paired.printers
            .filter((p: any) => p.target && p.target.trim() !== '')
            .map((p: any) => ({
              name: p.deviceName || p.name || 'EPSON Printer',
              address: p.target,
              brand: 'epson' as const,
              signalStrength: undefined,
              isPrinter: true,
            }));
        }
      } catch (_) {
        // getPairedBluetoothPrinters may not exist on older plugin versions
      }

      const result = await EposPlugin.searchPrinters();

      if (!result.printers || !Array.isArray(result.printers)) {
        return pairedList;
      }

      const epsonPrinters: DiscoveredPrinter[] = result.printers
        .filter((p: any) => {
          // Accept the device if it has any valid address-like field
          const addr = p.target || p.macAddress || p.bdAddress || p.leBdAddress || '';
          return addr.trim() !== '';
        })
        .map((p: any) => ({
          name: p.deviceName || p.name || p.target || 'EPSON Printer',
          // Prefer bdAddress for BT Classic, leBdAddress for BLE, fallback to target
          address: p.target || p.bdAddress || p.leBdAddress || p.macAddress,
          brand: 'epson' as const,
          signalStrength: p.rssi || p.signalStrength,
          isPrinter: true,
        }));

      // Merge: paired accessories first, then discovery results, deduplicated
      const merged = [...pairedList];
      for (const p of epsonPrinters) {
        if (!merged.some((m) => m.address === p.address)) {
          merged.push(p);
        }
      }
      return merged;
    } catch (error: any) {
      const msg = error?.message || 'Unknown Epson scan error';
      setScanError(msg);
      console.warn(`[Epson] ${msg}`);
      return [];
    }
  }, []);

  const scanStarPrinters = useCallback(async (): Promise<DiscoveredPrinter[]> => {
    try {
      const result = await StarXpandPlugin.searchPrinters();

      if (!result.printers || !Array.isArray(result.printers)) {
        return [];
      }

      const starPrinters = result.printers
        .filter((p: any) => p.identifier && p.identifier.trim() !== '')
        .map((p: any) => ({
          name: p.name || p.identifier || 'Star Printer',
          address: p.identifier,
          brand: 'star' as const,
          signalStrength: p.rssi || p.signalStrength,
          isPrinter: true,
        }));

      return starPrinters;
    } catch (error: any) {
      const msg = error?.message || 'Unknown Star scan error';
      setScanError(msg);
      console.warn(`[Star] ${msg}`);
      return [];
    }
  }, []);

  const deduplicatePrinters = useCallback((printers: DiscoveredPrinter[]): DiscoveredPrinter[] => {
    const seen = new Set<string>();
    const unique: DiscoveredPrinter[] = [];

    printers.forEach((printer) => {
      if (!seen.has(printer.address)) {
        seen.add(printer.address);
        unique.push(printer);
      }
    });

    return unique;
  }, []);

  const cancelRef = useRef(false);
  const isScanningRef = useRef(false);

  const cancelScan = useCallback(() => {
    if (!isScanningRef.current) return;
    cancelRef.current = true;
    try {
      setIsScanning(false);
      isScanningRef.current = false;
    } catch (e) {}
  }, []);

  const scanForPrinters = useCallback(async (): Promise<void> => {
    cancelRef.current = false;
    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

    try {
      let permRes: { granted?: boolean; permissions?: Record<string, boolean> } = { granted: true };
      const hasFn = EposPlugin && typeof (EposPlugin as any).hasRuntimePermissions === 'function';
      const reqFn = EposPlugin && typeof (EposPlugin as any).requestRuntimePermissions === 'function';

      if (hasFn) {
        permRes = await (EposPlugin as any).hasRuntimePermissions();
      }

      if ((!permRes || !permRes.granted) && reqFn) {
        try {
          const reqRes = await (EposPlugin as any).requestRuntimePermissions();
          if (reqRes && reqRes.granted) {
            await sleep(700);
            if (hasFn) {
              permRes = await (EposPlugin as any).hasRuntimePermissions();
            } else {
              permRes = reqRes;
            }
          } else {
            const msg = 'Required Bluetooth/location permissions are not granted. Please enable permissions in system settings.';
            setScanError(msg);
            throw new Error(msg);
          }
        } catch (e) {
          const msg = (e as Error)?.message || 'Permission request was cancelled or failed.';
          setScanError(msg);
          throw new Error(msg);
        }
      }

      if (!permRes || !permRes.granted) {
        const msg = 'Required Bluetooth/location permissions are not granted. Please enable permissions in system settings.';
        setScanError(msg);
        throw new Error(msg);
      }
    } catch (err) {
      const msg = (err as Error)?.message || 'Failed to verify permissions before scanning.';
      setScanError(msg);
      throw new Error(msg);
    }

    if (!bluetoothEnabled) {
      const errorMsg = 'Bluetooth is not enabled. Please enable Bluetooth in OS settings.';
      setScanError(errorMsg);
      throw new Error(errorMsg);
    }

    setIsScanning(true);
    isScanningRef.current = true;
    setScanError(null);
    setDiscoveredPrinters([]);

    try {
      const runWithTimeout = async (p: Promise<DiscoveredPrinter[]>) => {
        try {
          return (await Promise.race([
            p,
            new Promise<DiscoveredPrinter[]>((_, reject) => setTimeout(() => reject(new Error('Scan timeout')), SCAN_TIMEOUT_MS)),
          ])) as DiscoveredPrinter[];
        } catch (e) {
          return [];
        }
      };

      const [epsonPrinters, starPrinters] = await Promise.all([runWithTimeout(scanEpsonPrinters()), runWithTimeout(scanStarPrinters())]);

      if (cancelRef.current) {
        isScanningRef.current = false;
        return;
      }

      const allPrinters = [...epsonPrinters, ...starPrinters];
      const uniquePrinters = deduplicatePrinters(allPrinters);

      if (uniquePrinters.length === 0) {
        const noFoundMsg = 'No printers found. Make sure your printer is powered ON and Bluetooth is enabled.';
        setScanError(noFoundMsg);
      }

      setDiscoveredPrinters(uniquePrinters);
    } catch (error: any) {
      const errorMsg = error?.message || 'Failed to scan for printers';
      setScanError(errorMsg);
      throw error;
    } finally {
      if (!cancelRef.current) {
        setIsScanning(false);
        isScanningRef.current = false;
      } else {
        isScanningRef.current = false;
      }
    }
  }, [bluetoothEnabled, scanEpsonPrinters, scanStarPrinters, deduplicatePrinters]);

  const clearDiscovered = useCallback(() => {
    setDiscoveredPrinters([]);
    setScanError(null);
  }, []);

  return {
    isScanning,
    discoveredPrinters,
    scanError,
    scanForPrinters,
    clearDiscovered,
    scanEpsonPrinters,
    scanStarPrinters,
    cancelScan,
  };
};

export type { DiscoveredPrinter, PrinterBrand };
